var Cookie                    = require("js-cookie"),
    Service                   = require("app/Domain/Ott/Api/V2/Service"),
    circularSafeJSONStringify = require("app/utils/circularSafeJSONStringify"),
    config                    = require("app/config"),
    kind                      = require("enyo/kind"),
    logger                    = require("enyo/logger"),
    quickMacroize             = require("enyo/macroize").quickMacroize;

/**
 * This object retrieves WatchingSessions from the API.
 */
module.exports = kind.singleton(/** @lends app.Domain.Ott.Api.V2.DataAccess.WatchingSession.Service.prototype */ {

    /**
     * @private
     */
    name: "app.Domain.Ott.Api.V2.DataAccess.WatchingSession.Service",

    /**
     * @private
     */
    kind: Service,

    // API
    // --------------------------------

    /**
     * @param    {number}   language    The language
     * @param    {string}   mediaType   The media type
     * @param    {string}   mediaSlug   The media slug
     * @param    {string=}  categoryId  Optional category id
     * @param    {string=}  isAdult     Optional age flag
     * @returns  {Promise}  A Promise that resolves with the API response
     * @public
     */
    requestBySlug: function(language, mediaType, mediaSlug, categoryId, isAdult) {
        var key  = ["watching-session", "request", language, mediaType, mediaSlug, categoryId, isAdult].join("/"),
            task = this.tasks[key],
            endpoint;

        if (!task) {
            endpoint = new URL(quickMacroize("{$ottApi}/watching-session/request?language={$language}", {
                ottApi:   config.ottApi.url,
                language: encodeURIComponent(language)
            }));

            if (categoryId) {
                endpoint.searchParams.set("category_id", categoryId);
            }

            if (typeof isAdult === "boolean") {
                endpoint.searchParams.set("is_adult", isAdult);
            }

            task = this.tasks[key] = this._post(endpoint.toString(), {
                data: {
                    type: mediaType,
                    slug: mediaSlug
                }
            });

            task.promise
                .then(this.bindSafely(function(response) {
                    if (response.status === "ok"
                        && response.hasOwnProperty("data")
                        && typeof response.data === "object") {
                        response.data.cookies = {
                            "CloudFront-Policy":      Cookie.get("CloudFront-Policy"),
                            "CloudFront-Signature":   Cookie.get("CloudFront-Signature"),
                            "CloudFront-Key-Pair-Id": Cookie.get("CloudFront-Key-Pair-Id")
                        };
                    }

                    return response;
                }))
                .catch(this.bindSafely(function(error) {
                    logger.error(error);

                    if (typeof Sentry !== "undefined" && error instanceof Error) {
                        Sentry.captureException(error);
                    }

                    return Promise.resolve({
                        status: "rejected",
                        type:   "task_rejected",
                        error:  error
                    });
                }))
                .finally(this.bindSafely(function() {
                    delete this.tasks[key];
                }));
        }

        return task.promise;
    },

    /**
     * @param    {number}   language    The language
     * @param    {string}   mediaType   The media type
     * @param    {string}   mediaId     The media id
     * @param    {string=}  categoryId  Optional category id
     * @returns  {Promise}  A Promise that resolves with the API response
     * @public
     */
    requestById: function(language, mediaType, mediaId, categoryId) {
        var key  = ["watching-session", "request", language, mediaType, mediaId, categoryId].join("/"),
            task = this.tasks[key],
            endpoint;

        if (!task) {
            endpoint = new URL(quickMacroize("{$ottApi}/watching-session/request?language={$language}", {
                ottApi:   config.ottApi.url,
                language: encodeURIComponent(language)
            }));

            if (categoryId) {
                endpoint.searchParams.set("category_id", categoryId);
            }

            task = this.tasks[key] = this._post(endpoint.toString(), {
                data: {
                    type: mediaType,
                    id:   mediaId
                }
            });

            task.promise
                .then(this.bindSafely(function(response) {
                    if (response.status === "ok"
                        && response.hasOwnProperty("data")
                        && typeof response.data === "object") {
                        response.data.cookies = {
                            "CloudFront-Policy":      Cookie.get("CloudFront-Policy"),
                            "CloudFront-Signature":   Cookie.get("CloudFront-Signature"),
                            "CloudFront-Key-Pair-Id": Cookie.get("CloudFront-Key-Pair-Id")
                        };
                    }

                    return response;
                }))
                .catch(this.bindSafely(function(error) {
                    logger.error(error);

                    if (typeof Sentry !== "undefined" && error instanceof Error) {
                        Sentry.captureException(error);
                    }

                    return Promise.resolve({
                        status: "rejected",
                        type:   "task_rejected",
                        error:  error
                    });
                }))
                .finally(this.bindSafely(function() {
                    delete this.tasks[key];
                }));
        }

        return task.promise;
    },

    /**
     * Request session with mediaType and mediaSlug
     *
     * @param    {number}   cursor    The cursor position
     * @param    {string}   endpoint  The endpoint to submit the cursor to
     * @returns  {Promise}  Resolves with the new session state
     * @public
     */
    updateCursor: function(cursor, endpoint) {
        var task = this.tasks[endpoint];

        // Abort old task
        task && task.abort();

        task = this.tasks[endpoint] = this._buildXhrTask();

        task.execute = function() {
            var xhr;

            task.xhr = xhr = new XMLHttpRequest();
            xhr.open("POST", endpoint);
            xhr.setRequestHeader("Content-Type", "text/plain");

            xhr.onreadystatechange = function() {
                if (xhr.readyState === 4) {
                    task.handleResult();
                }
            };

            xhr.onabort            = function() {
                if (task.isRenewing) {
                    // We are renewing the task, we can skip handling the
                    // canceled response and await the result of the renewed
                    // task.
                }
                else {
                    task.handleResult();
                }
            };
            xhr.onreadystatechange.bind(this);
            xhr.send(String(parseFloat(cursor, 10)));
        };

        task.handleResult = function() {
            var response,
                xhr     = task.xhr,
                resolve = task.resolve;

            try {
                response = xhr.responseText || null;
            }
            catch (error) {
                logger.error(error);

                if (typeof Sentry !== "undefined" && error instanceof Error) {
                    Sentry.captureException(error, {
                        extra: {
                            xhr: circularSafeJSONStringify(xhr)
                        }
                    });
                }
                response = null;
            }

            switch (xhr.status) {
                case 0:
                    resolve({
                        status: "canceled"
                    });
                    break;
                case 200:
                    resolve({
                        status: "ok"
                    });
                    break;
                case 400:
                    resolve({
                        status: "error",
                        type:   "bad_cursor"
                    });
                    break;
                case 403:
                    resolve({
                        status: "error",
                        type:   response || "invalid_session"
                    });
                    break;
                default:
                    resolve({
                        status:     "error",
                        type:       response && response ? response : "server_error",
                        httpStatus: xhr.status
                    });
                    break;
            }
        };

        task.promise
            .then(this.bindSafely(function(response) {
                return response;
            }))
            .catch(this.bindSafely(function(error) {
                logger.error(error);

                if (typeof Sentry !== "undefined" && error instanceof Error) {
                    Sentry.captureException(error);
                }

                return Promise.resolve({
                    status: "rejected",
                    type:   "task_rejected",
                    error:  error
                });
            }))
            .finally(this.bindSafely(function() {
                delete this.tasks[endpoint];
            }));

        // Run the task
        task.execute();

        return task.promise;
    }

});
